'use client';

import { ErrorBoundary } from '@sentry/nextjs';
import { AnimatePresence, motion } from 'framer-motion';
import { useRouter } from 'next/navigation';
import type { ChangeEvent, FormEvent} from 'react';
import { useState } from 'react';

import { Search } from '@zoomit/icons';
import { Container, useDebounce } from '@zoomit/primitives';
import { isMobile, onSearchTerm, SearchDesktopBody, SomethingWentWrong } from '@zoomit/web-kit';
import { cssVar, Input, theme } from '@zoomit/zoomit-ds';

import { useWebKitCustomization } from '../../../hooks/useWebKitCustomization';
import type { MenuParserData } from '../../../types/menus';
import { SearchInstance } from '../SearchInstance';

type Props = {
  isSearchOpen: boolean;
  setIsSearchOpen: (v: boolean) => void;
  searchInstanceRenderer?: MenuParserData['searchInstanceRenderer'];
};

export const DefaultMenuSearchSection = (props: Props) => {
  const { isSearchOpen, setIsSearchOpen, searchInstanceRenderer } = props;
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const redirectToSearch = (e: FormEvent<HTMLFormElement>) => {
    const form = new FormData(e.currentTarget);
    e.preventDefault();
    const query = form.get('search');
    setIsSearchOpen(false);
    router.push(`/search/${query}`);
  };

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    if (event.target.value.length !== 0) onSearchTerm(event.target.value);
  };

  const animateVariants = {
    initial: {
      y: -15,
      opacity: 0,
    },
    open: {
      y: 0,
      opacity: 1,
    },
  };

  const searchSettings = useWebKitCustomization((settings) => settings.search);

  return (
    <AnimatePresence>
      {isSearchOpen && (
        <SearchDesktopBody isSearchActive={isSearchOpen} setIsSearchActive={setIsSearchOpen}>
          <ErrorBoundary fallback={<SomethingWentWrong />}>
            <Container className={'h-full w-full max-w-[872px]'} spacing={{ pt: 'x6', px: 'x6', pb: 'x6' }}>
              <motion.form
                transition={{
                  delay: 0.2,
                  duration: 0.3,
                }}
                variants={animateVariants}
                initial={isMobile ? 'open' : 'initial'}
                animate={'open'}
                onSubmit={redirectToSearch}
              >
                <Input
                  name="search"
                  variant="rounded"
                  rightNode={<Search fill={cssVar(theme.text)} />}
                  placeholder={searchSettings?.placeholder || 'جستجو ...'}
                  value={searchQuery}
                  onChange={handleSearch}
                  autoFocus
                />
                {searchInstanceRenderer ? (
                  searchInstanceRenderer({
                    onClose: () => setIsSearchOpen(false),
                    searchQuery: debouncedSearchQuery,
                  })
                ) : (
                  <SearchInstance searchQuery={debouncedSearchQuery} onClose={() => setIsSearchOpen(false)} />
                )}
              </motion.form>
            </Container>
          </ErrorBoundary>
        </SearchDesktopBody>
      )}
    </AnimatePresence>
  );
};
